import { IUISupplier } from '@bridebook/toolbox/src/types';
import { getSupplierL10nAnalytics } from '@bridebook/web/lib/analytics-utils/get-supplier-l10n';
import { IApplicationState } from '@bridebook/web/lib/types';
import { IServerSideTrack } from '@bridebook/web/lib/utils/server-side-track';
import enquirySupplierTrack from '@bridebook/web/lib/utils/server-side-track/enquiry-supplier-track';
import { weddingDetailsPropertiesGeneric } from '@bridebook/web/lib/weddings/analytics';
import { CriticalWebEvents } from '../../analyticsTypes';
import getEnquiriesAnalyticsProps from './get-analytics-props';
import { getConversationId } from '@bridebook/toolbox/src/inbox/api/talkjs/get-conversation-id';
import { EnquiryIntents } from '@bridebook/web/lib/enquiries/intent';

interface IEnquiriesServerSideTrack {
  event: string;
  id?: string;
  state: IApplicationState;
  contactedSupplier: IUISupplier;
  infoProps?: Record<string, any>;
  contactType?: string;
  contactMethod?: string;
  linkedSupplierName?: string;
  linkedSupplierId?: string;
  message?: string;
  intent?: EnquiryIntents;
  defaultIntentsSelected?: boolean;
  defaultMessageTyped?: boolean;
}

const enquiriesServerSideTrack = ({
  event,
  id,
  state,
  contactedSupplier,
  infoProps = {},
  contactType,
  contactMethod,
  linkedSupplierName,
  linkedSupplierId,
  message,
  intent,
  defaultIntentsSelected,
  defaultMessageTyped,
}: IEnquiriesServerSideTrack): Promise<any> => {
  const {
    users: { user },
    weddings: {
      profile: { id: weddingId },
    },
    enquiries: { enquirySupplier, multipleEnquiry, infoProps: stateInfoProps },
  } = state;

  const supplier = contactedSupplier || enquirySupplier;

  const infoPropsMerged = {
    ...stateInfoProps,
    ...Object.fromEntries(Object.entries(infoProps).filter(([, value]) => !!value)), // Avoid falsey values
  };

  const analyticsProps: IServerSideTrack = {
    state,
    event,
    category: 'Supplier Engagement',
    specificEventProps: {
      enquiryId: id || undefined,
      weddingId,
      ...getEnquiriesAnalyticsProps({
        supplier,
        getState: () => state,
        infoProps: infoPropsMerged,
        message,
        intent,
      }),
      ...weddingDetailsPropertiesGeneric(state),
      ...(contactType ? { contactType } : {}),
      ...(contactMethod ? { contactMethod } : {}),
      ...(linkedSupplierName ? { linkedSupplierName } : {}),
      ...(linkedSupplierId ? { linkedSupplierId } : {}),
    },
    contactedSupplier: supplier,
  };

  if (!analyticsProps.specificEventProps) {
    analyticsProps.specificEventProps = {};
  }

  const { supplierCountryCode, supplierCountryName, adminArea0, adminArea1 } =
    getSupplierL10nAnalytics(supplier);

  switch (event) {
    case 'Failed to send enquiry':
      analyticsProps.specificEventProps.failedToSendEnquiryReason = infoProps.errorMessage;
      break;
    case 'Failed to confirm details':
      analyticsProps.specificEventProps.failedToConfirmDetailsReason = infoProps.errorMessage || '';
      break;
    case 'Confirmed enquiry details':
      analyticsProps.specificEventProps.enquiryDetailsConfirmLocation =
        infoProps.enquiryDetailsConfirmLocation || '';
      break;
    case 'Closed enquiry form':
      analyticsProps.specificEventProps.closedEnquiryFormMethod = infoProps.closeMethod || '';
      break;
    case CriticalWebEvents.EMAILED_SUPPLIER:
      if (multipleEnquiry) {
        analyticsProps.specificEventProps.contactType = 'multipleEnquiry';
      }

      if (!analyticsProps.specificEventProps.contactType) {
        analyticsProps.specificEventProps.contactType = 'singleEnquiry';
      }

      analyticsProps.specificEventProps.supplierCountryCode = supplierCountryCode;
      analyticsProps.specificEventProps.supplierCountryName = supplierCountryName;
      analyticsProps.specificEventProps.adminArea0 = adminArea0;
      analyticsProps.specificEventProps.adminArea1 = adminArea1;

      analyticsProps.specificEventProps.conversationId = getConversationId([
        supplier.id,
        weddingId,
      ]);

      if (defaultIntentsSelected !== undefined) {
        analyticsProps.specificEventProps.defaultIntentsSelected = defaultIntentsSelected;
      }

      if (defaultMessageTyped !== undefined) {
        analyticsProps.specificEventProps.defaultMessageTyped = defaultMessageTyped;
      }

      break;
    default:
      break;
  }

  const userEmail = user?.contacts?.email;

  if (userEmail) {
    analyticsProps.identifyProps = { userEmail };
  }
  return enquirySupplierTrack(analyticsProps);
};

export default enquiriesServerSideTrack;
